import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import AdminPortal from './AdminPortal';
import OwnerPortal from './OwnerPortal';
import HelpPage from './HelpPage';
import Footer from './Footer';
import Analytics from './Analytics';
import SEO from './SEO';
import { initAdSense } from './AdSense';

// Icon Components
const Icon = ({ name, className = "w-4 h-4" }) => {
  const icons = {
    mapPin: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z",
    search: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
    plus: "M12 4v16m8-8H4",
    checkCircle: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
    alertCircle: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    xCircle: "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z",
    trendingDown: "M13 17h8m0 0V9m0 8l-8-8-4 4-6-6",
    clock: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
    chevronLeft: "M15 19l-7-7 7-7",
    chevronRight: "M9 5l7 7-7 7",
    help: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
  };
  
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[name]} />
    </svg>
  );
};

// Constants
const FUEL_TYPES = [
  { code: 'PMS', name: 'Petrol (PMS)', unit: '₦/Litre' },
  { code: 'AGO', name: 'Diesel (AGO)', unit: '₦/Litre' },
  { code: 'DPK', name: 'Kerosene (DPK)', unit: '₦/Litre' },
  { code: 'LPG', name: 'Cooking Gas (LPG)', unit: '₦/kg' }
];

const NIGERIAN_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 
  'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 
  'Ekiti', 'Enugu', 'FCT Abuja', 'Gombe', 'Imo', 'Jigawa', 
  'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 
  'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 
  'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
].sort();

// Custom Hooks
const useLocalStorage = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    const saved = localStorage.getItem(key);
    return saved || defaultValue;
  });

  useEffect(() => {
    localStorage.setItem(key, value);
  }, [key, value]);

  return [value, setValue];
};

const useStations = () => {
  const [stations, setStations] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'stations'), orderBy('lastUpdated', 'desc'));
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const stationsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setStations(stationsData);
      },
      (error) => console.error('Error loading stations:', error)
    );
    return () => unsubscribe();
  }, []);

  return stations;
};

// Utility Functions
const getAvailabilityStyle = (status) => {
  const styles = {
    available: 'text-green-600 bg-green-50',
    limited: 'text-yellow-600 bg-yellow-50',
    out: 'text-red-600 bg-red-50'
  };
  return styles[status] || 'text-gray-600 bg-gray-50';
};

const getAvailabilityIcon = (status) => {
  const icons = {
    available: 'checkCircle',
    limited: 'alertCircle',
    out: 'xCircle'
  };
  return icons[status] || 'alertCircle';
};

const getQueueStyle = (queue) => {
  const styles = {
    none: 'bg-green-100 text-green-800',
    short: 'bg-blue-100 text-blue-800',
    moderate: 'bg-yellow-100 text-yellow-800',
    long: 'bg-red-100 text-red-800'
  };
  return styles[queue] || 'bg-gray-100 text-gray-800';
};

// Sub-Components
const Header = ({ selectedState, setSelectedState, filteredCount }) => (
  <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4">
    <div className="flex flex-col items-center mb-3">
      <div className="bg-white rounded-lg p-3 mb-2 shadow-lg">
        <img 
          src="/logo.png" 
          alt="Naija Petrol Prices" 
          className="h-28 w-auto" 
          onError={(e) => e.target.parentElement.innerHTML = '<div class="text-center px-4 py-2"><h1 class="text-3xl font-bold text-green-700">Naija Petrol Prices</h1></div>'} 
        />
      </div>
    </div>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon name="mapPin" className="w-4 h-4 text-green-100" />
        <select 
          value={selectedState} 
          onChange={(e) => setSelectedState(e.target.value)}
          className="bg-green-800 text-white border border-green-500 rounded-lg px-3 py-2 text-sm"
        >
          {NIGERIAN_STATES.map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
        <span className="text-xs text-green-100 ml-2">({filteredCount} stations)</span>
      </div>
      <a
        href="/help"
        className="flex items-center gap-1 bg-green-800 hover:bg-green-900 px-3 py-2 rounded-lg text-sm transition-colors"
      >
        <Icon name="help" />
        Help
      </a>
    </div>
  </div>
);

const TabNav = ({ activeTab, setActiveTab }) => (
  <div className="border-b bg-white sticky top-0 z-20">
    <div className="flex">
      {[
        { id: 'list', label: '📋 List View' },
        { id: 'map', label: '🗺️ Map View' }
      ].map(tab => (
        <button 
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`flex-1 py-3 font-medium ${
            activeTab === tab.id 
              ? 'border-b-2 border-green-600 text-green-600' 
              : 'text-gray-600'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  </div>
);

const FuelTypeSelector = ({ selectedFuelType, setSelectedFuelType }) => (
  <div className="border-b bg-white">
    <div className="flex overflow-x-auto">
      {FUEL_TYPES.map(type => (
        <button 
          key={type.code} 
          onClick={() => setSelectedFuelType(type.code)}
          className={`flex-1 px-6 py-4 font-medium ${
            selectedFuelType === type.code 
              ? 'border-b-2 border-green-600 text-green-600' 
              : 'text-gray-600'
          }`}
        >
          <div className="text-sm">{type.name}</div>
          <div className="text-xs text-gray-500 mt-1">{type.unit}</div>
        </button>
      ))}
    </div>
  </div>
);

const PriceStats = ({ lowestPrice, averagePrice }) => (
  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50">
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="text-xs text-gray-500 mb-1">Lowest Price</div>
      <div className="text-2xl font-bold text-green-600">₦{lowestPrice}</div>
    </div>
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="text-xs text-gray-500 mb-1">Average Price</div>
      <div className="text-2xl font-bold text-gray-900">₦{averagePrice.toFixed(0)}</div>
    </div>
  </div>
);

const SearchBar = ({ searchQuery, setSearchQuery, selectedState, setShowAddStationForm, resultsCount }) => (
  <div className="p-4">
    <div className="relative mb-3">
      <Icon name="search" className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
      <input 
        type="text" 
        placeholder="Search by name, city, or area..." 
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full pl-10 pr-4 py-3 border rounded-lg" 
      />
    </div>
    {searchQuery && (
      <p className="text-xs text-gray-600 mb-3">
        Found {resultsCount} station{resultsCount !== 1 ? 's' : ''} matching "{searchQuery}"
      </p>
    )}
    <button
      onClick={() => setShowAddStationForm(true)}
      className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 flex items-center justify-center gap-2"
    >
      <Icon name="plus" className="w-5 h-5" />
      Add New Station to {selectedState}
    </button>
  </div>
);

const PageSizeSelector = ({ itemsPerPage, setItemsPerPage, setCurrentPage }) => {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customPageSize, setCustomPageSize] = useState('');

  const handleCustomSize = () => {
    const size = parseInt(customPageSize);
    if (size >= 5 && size <= 100) {
      setItemsPerPage(size);
      setCurrentPage(1);
    } else {
      alert('Please enter a number between 5 and 100');
    }
  };

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-b">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">Stations per page:</span>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {[10, 25, 50].map(size => (
              <button
                key={size}
                onClick={() => {
                  setItemsPerPage(size);
                  setShowCustomInput(false);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1.5 text-sm rounded ${
                  itemsPerPage === size && !showCustomInput
                    ? 'bg-green-600 text-white'
                    : 'bg-white border hover:bg-gray-50'
                }`}
              >
                {size}
              </button>
            ))}
            <button
              onClick={() => {
                setShowCustomInput(!showCustomInput);
                if (showCustomInput) {
                  setItemsPerPage(20);
                  setCustomPageSize('');
                  setCurrentPage(1);
                }
              }}
              className={`px-3 py-1.5 text-sm rounded ${
                showCustomInput
                  ? 'bg-green-600 text-white'
                  : 'bg-white border hover:bg-gray-50'
              }`}
            >
              Custom
            </button>
          </div>
          
          {showCustomInput && (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="5"
                max="100"
                value={customPageSize}
                onChange={(e) => setCustomPageSize(e.target.value)}
                placeholder="Enter"
                className="w-20 px-2 py-1.5 text-sm border rounded"
              />
              <button
                onClick={handleCustomSize}
                disabled={!customPageSize}
                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>
      {!showCustomInput && itemsPerPage === 20 && (
        <p className="text-xs text-gray-500 mt-1">Default: 20 per page</p>
      )}
    </div>
  );
};

const StationCard = ({ station, selectedFuelType, lowestPrice, setShowReportForm }) => {
  const isLowest = station.prices?.[selectedFuelType] === lowestPrice;
  
  return (
    <div className="bg-white border rounded-lg p-4 shadow-sm">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-lg">{station.name}</h3>
            {station.verified && (
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded">Verified</span>
            )}
            {isLowest && (
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded flex items-center gap-1">
                <Icon name="trendingDown" className="w-3 h-3" />
                Lowest
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-1">{station.address}</p>
          {station.lat && station.lng && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${station.lat},${station.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
            >
              <Icon name="mapPin" className="w-3 h-3" />
              View on Google Maps
            </a>
          )}
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">₦{station.prices?.[selectedFuelType] || 0}</div>
          <div className="text-xs text-gray-500">per litre</div>
        </div>
      </div>
      
      <div className="flex items-center gap-3 mb-3">
        <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getAvailabilityStyle(station.availability?.[selectedFuelType])}`}>
          <Icon name={getAvailabilityIcon(station.availability?.[selectedFuelType])} />
          {station.availability?.[selectedFuelType]}
        </div>
        <div className={`px-2 py-1 rounded text-xs font-medium ${getQueueStyle(station.queue)}`}>
          Queue: {station.queue}
        </div>
      </div>
      
      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
        <span className="flex items-center gap-1">
          <Icon name="clock" className="w-3 h-3" />
          Updated {new Date(station.lastUpdated).toLocaleDateString('en-NG', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
          })} at {new Date(station.lastUpdated).toLocaleTimeString('en-NG', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </span>
        {station.updatedBy && (
          <span className={`flex items-center gap-1 ${station.updatedByOwner ? 'text-green-700 font-semibold' : 'text-blue-600'}`}>
            {station.updatedByOwner && (
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
            by {station.updatedByOwner ? 'Owner' : station.updatedBy}
          </span>
        )}
      </div>

      <button
        onClick={() => {
          console.log('Update button clicked for station:', station.id);
          setShowReportForm(station.id);
        }}
        className="w-full bg-green-50 text-green-700 py-2 rounded-lg text-sm font-medium hover:bg-green-100 flex items-center justify-center gap-2"
      >
        <Icon name="plus" className="w-4 h-4" />
        Update Price & Availability
      </button>
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, setCurrentPage, startIndex, endIndex, totalItems, itemsPerPage }) => (
  <div className="mt-6 pt-4 border-t">
    <div className="flex items-center justify-between mb-3">
      <div className="text-sm text-gray-600">
        Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} stations
        <span className="text-xs text-gray-500 ml-2">({itemsPerPage} per page)</span>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <Icon name="chevronLeft" />
          Previous
        </button>
        
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-2 rounded-lg ${
                  currentPage === pageNum
                    ? 'bg-green-600 text-white'
                    : 'border hover:bg-gray-50'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
        
        <button
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          Next
          <Icon name="chevronRight" />
        </button>
      </div>
    </div>
  </div>
);

const ReportForm = ({ station, selectedFuelType, onClose, onSubmit }) => {
  console.log('ReportForm rendering with station:', station);
  console.log('selectedFuelType:', selectedFuelType);
  
  const [price, setPrice] = useState(station.prices?.[selectedFuelType] || 0);
  const [availability, setAvailability] = useState(station.availability?.[selectedFuelType] || 'available');
  const [queue, setQueue] = useState(station.queue || 'none');
  const [location, setLocation] = useState(null);
  const [userName, setUserName] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);

  const captureLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setGettingLocation(false);
          alert('Location captured!');
        },
        (error) => {
          console.error('Location error:', error);
          setGettingLocation(false);
        }
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <h3 className="text-xl font-bold mb-4">Update Station Info</h3>
        <p className="text-sm text-gray-600 mb-4">{station.name}</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Your Name (Optional)</label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your name"
              className="w-full border rounded-lg px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">This will show as "Updated by: Your Name"</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Price per {selectedFuelType === 'LPG' ? 'kg' : 'Litre'} (₦)</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Fuel Availability</label>
            <select
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="available">Available</option>
              <option value="limited">Limited Supply</option>
              <option value="out">Out of Stock</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Queue Status</label>
            <select
              value={queue}
              onChange={(e) => setQueue(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="none">No Queue</option>
              <option value="short">Short Queue (&lt;5 mins)</option>
              <option value="moderate">Moderate Queue (5-15 mins)</option>
              <option value="long">Long Queue (&gt;15 mins)</option>
            </select>
          </div>

          <div className="border rounded-lg p-3 bg-blue-50">
            <button
              type="button"
              onClick={captureLocation}
              disabled={gettingLocation}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 text-sm flex items-center justify-center gap-2"
            >
              <Icon name="mapPin" />
              {gettingLocation ? 'Getting Location...' : location ? '✓ Location Captured' : 'Verify Location (Optional)'}
            </button>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => onSubmit(station.id, price, availability, queue, location, userName)}
              className="flex-1 bg-green-600 text-white rounded-lg py-2 font-medium hover:bg-green-700"
            >
              Submit Update
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 rounded-lg py-2 font-medium hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddStationForm = ({ selectedState, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: selectedState,
    lat: null,
    lng: null,
    userName: '',
    pmsPrice: '',
    agoPrice: '',
    dpkPrice: '',
    lpgPrice: '',
    pmsAvailability: 'available',
    agoAvailability: 'available',
    dpkAvailability: 'available',
    lpgAvailability: 'available',
    queue: 'none'
  });
  const [gettingLocation, setGettingLocation] = useState(false);

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const captureLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setGettingLocation(false);
          alert('Location captured successfully!');
        },
        (error) => {
          console.error('Location error:', error);
          alert('Unable to get location. Please enable location services.');
          setGettingLocation(false);
        }
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8 shadow-2xl">
        <h3 className="text-xl font-bold mb-4">Add New Filling Station</h3>
        <p className="text-sm text-gray-600 mb-6">Help the community by adding a station. Changes are live immediately!</p>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Station Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g., Total Energies"
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">State *</label>
              <select
                value={formData.state}
                onChange={(e) => updateField('state', e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              >
                {NIGERIAN_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">City/Area *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => updateField('city', e.target.value)}
                placeholder="e.g., Ikeja"
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Address *</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="e.g., Allen Avenue"
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Your Name (Optional)</label>
            <input
              type="text"
              value={formData.userName}
              onChange={(e) => updateField('userName', e.target.value)}
              placeholder="Enter your name"
              className="w-full border rounded-lg px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">This will show who added this station</p>
          </div>

          <div className="border rounded-lg p-4 bg-blue-50">
            <label className="block text-sm font-medium mb-2">Location Coordinates</label>
            <p className="text-xs text-gray-600 mb-3">
              Capture your current location to help users find this station on the map
            </p>
            <button
              type="button"
              onClick={captureLocation}
              disabled={gettingLocation}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              <Icon name="mapPin" />
              {gettingLocation ? 'Getting Location...' : formData.lat ? '✓ Location Captured' : 'Use My Current Location'}
            </button>
            {formData.lat && (
              <p className="text-xs text-green-700 mt-2">
                📍 Lat: {formData.lat.toFixed(6)}, Lng: {formData.lng.toFixed(6)}
              </p>
            )}
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-3">Fuel Prices</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FUEL_TYPES.map(type => (
                <div key={type.code}>
                  <label className="block text-xs text-gray-600 mb-1">
                    {type.name.split(' (')[0]} ({type.unit})
                  </label>
                  <input
                    type="number"
                    value={formData[`${type.code.toLowerCase()}Price`]}
                    onChange={(e) => updateField(`${type.code.toLowerCase()}Price`, e.target.value)}
                    placeholder={type.code === 'PMS' ? '650' : type.code === 'AGO' ? '1200' : type.code === 'DPK' ? '950' : '1400'}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-3">Availability Status</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FUEL_TYPES.map(type => (
                <div key={type.code}>
                  <label className="block text-xs text-gray-600 mb-1">{type.code}</label>
                  <select
                    value={formData[`${type.code.toLowerCase()}Availability`]}
                    onChange={(e) => updateField(`${type.code.toLowerCase()}Availability`, e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="available">Available</option>
                    <option value="limited">Limited</option>
                    <option value="out">Out of Stock</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Current Queue Status</label>
            <select
              value={formData.queue}
              onChange={(e) => updateField('queue', e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="none">No Queue</option>
              <option value="short">Short Queue (&lt;5 mins)</option>
              <option value="moderate">Moderate Queue (5-15 mins)</option>
              <option value="long">Long Queue (&gt;15 mins)</option>
            </select>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => onSubmit(formData)}
              disabled={!formData.name || !formData.address || !formData.city}
              className="flex-1 bg-green-600 text-white rounded-lg py-2 font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add Station
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 rounded-lg py-2 font-medium hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MapView = ({ selectedState, selectedFuelType, filteredStations, lowestPrice }) => (
  <div className="p-4">
    <div className="bg-white rounded-lg border overflow-hidden mb-4" style={{ height: '500px' }}>
      <iframe
        width="100%"
        height="100%"
        frameBorder="0"
        style={{ border: 0 }}
        src={`https://www.google.com/maps/embed/v1/search?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=fuel+stations+in+${encodeURIComponent(selectedState + ', Nigeria')}&zoom=11`}
        allowFullScreen
        title="Fuel Stations Map"
      />
    </div>
    
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-lg">Stations in {selectedState}</h3>
        <span className="text-sm text-gray-600">{filteredStations.length} stations</span>
      </div>
      
      {filteredStations.map(station => {
        const isLowest = station.prices?.[selectedFuelType] === lowestPrice;
        return (
          <div key={station.id} className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="mapPin" className="w-4 h-4 text-green-600" />
                  <h4 className="font-bold">{station.name}</h4>
                  {isLowest && (
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded flex items-center gap-1">
                      <Icon name="trendingDown" className="w-3 h-3" />
                      Lowest
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 ml-5">{station.address}, {station.city}</p>
                {station.lat !== 0 && station.lng !== 0 && (
                  <p className="text-xs text-gray-500 ml-5 mt-1">
                    📍 {station.lat.toFixed(4)}, {station.lng.toFixed(4)}
                  </p>
                )}
              </div>
              <div className="text-right ml-4">
                <div className="text-2xl font-bold text-green-600">₦{station.prices?.[selectedFuelType] || 0}</div>
                <div className="text-xs text-gray-500">{selectedFuelType}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mt-3">
              <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getAvailabilityStyle(station.availability?.[selectedFuelType])}`}>
                <Icon name={getAvailabilityIcon(station.availability?.[selectedFuelType])} />
                {station.availability?.[selectedFuelType]}
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium ${getQueueStyle(station.queue)}`}>
                Queue: {station.queue}
              </div>
            </div>
            
            {station.lat !== 0 && station.lng !== 0 && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${station.lat},${station.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full bg-blue-50 text-blue-700 py-2 rounded-lg text-sm font-medium hover:bg-blue-100 flex items-center justify-center gap-2"
              >
                <Icon name="mapPin" />
                Open in Google Maps
              </a>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// Main App Component
function App() {
  // Check for special routes FIRST (before any hooks)
  const isAdminRoute = window.location.pathname === '/admin';
  const isOwnerRoute = window.location.pathname === '/owner';
  const isHelpRoute = window.location.pathname === '/help';

  // All hooks must be called unconditionally, before any early returns
  const [activeTab, setActiveTab] = useLocalStorage('activeTab', 'list');
  const [selectedFuelType, setSelectedFuelType] = useLocalStorage('selectedFuelType', 'PMS');
  const [selectedState, setSelectedState] = useLocalStorage('selectedState', 'Lagos');
  const [searchQuery, setSearchQuery] = useState('');
  const [showReportForm, setShowReportForm] = useState(false);
  const [showAddStationForm, setShowAddStationForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const stations = useStations();

  useEffect(() => {
    initAdSense();
  }, []);

  useEffect(() => {
    console.log('showReportForm changed to:', showReportForm);
  }, [showReportForm]);

  // Filter stations
  const filteredStations = stations
    .filter(station => {
      const matchesState = station.state === selectedState;
      const searchLower = searchQuery.toLowerCase().trim();
      
      if (!searchLower) return matchesState && !station.pendingModeration;
      
      const matchesSearch = 
        station.name?.toLowerCase().includes(searchLower) ||
        station.city?.toLowerCase().includes(searchLower) ||
        station.address?.toLowerCase().includes(searchLower);
      
      return matchesState && matchesSearch && !station.pendingModeration;
    })
    .sort((a, b) => (a.prices?.[selectedFuelType] || 0) - (b.prices?.[selectedFuelType] || 0));

  // Calculate stats
  const averagePrice = filteredStations.length > 0 
    ? filteredStations.reduce((sum, s) => sum + (s.prices?.[selectedFuelType] || 0), 0) / filteredStations.length 
    : 0;

  const lowestPrice = filteredStations.length > 0 
    ? Math.min(...filteredStations.map(s => s.prices?.[selectedFuelType] || Infinity)) 
    : 0;

  // Pagination
  const totalPages = Math.ceil(filteredStations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStations = filteredStations.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedState]);

  // NOW we can do conditional returns (after all hooks are called)
  if (isAdminRoute) return <AdminPortal />;
  if (isOwnerRoute) return <OwnerPortal />;
  if (isHelpRoute) return <HelpPage />;


  const handleReportPrice = async (stationId, newPrice, availability, queue, location, userName) => {
    try {
      const stationRef = doc(db, 'stations', stationId);
      const station = stations.find(s => s.id === stationId);
      
      await updateDoc(stationRef, {
        prices: { ...station.prices, [selectedFuelType]: newPrice },
        availability: { ...station.availability, [selectedFuelType]: availability },
        queue: queue,
        lastUpdated: new Date().toISOString(),
        updatedBy: userName || 'Anonymous User',
        updatedByOwner: false,
        ...(location && { lat: location.lat, lng: location.lng })
      });
      
      setShowReportForm(false);
      alert('✅ Price updated successfully! All users will see this update immediately.');
    } catch (error) {
      console.error('Error updating price:', error);
      alert('❌ Failed to update. Please try again.');
    }
  };

  const handleAddStation = async (formData) => {
    try {
      await addDoc(collection(db, 'stations'), {
        name: formData.name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        lat: formData.lat || 0,
        lng: formData.lng || 0,
        prices: {
          PMS: parseInt(formData.pmsPrice) || 0,
          AGO: parseInt(formData.agoPrice) || 0,
          DPK: parseInt(formData.dpkPrice) || 0,
          LPG: parseInt(formData.lpgPrice) || 0
        },
        availability: {
          PMS: formData.pmsAvailability || 'available',
          AGO: formData.agoAvailability || 'available',
          DPK: formData.dpkAvailability || 'available',
          LPG: formData.lpgAvailability || 'available'
        },
        lastUpdated: new Date().toISOString(),
        queue: formData.queue || 'none',
        verified: false,
        pendingModeration: false,
        reportCount: 0,
        createdBy: formData.userName || 'Anonymous User',
        updatedBy: formData.userName || 'Anonymous User',
        ownerCode: Math.random().toString(36).substring(2, 10).toUpperCase(),
        claimedBy: null
      });
      
      setShowAddStationForm(false);
      alert('✅ Station added successfully! It\'s now visible to all users.');
    } catch (error) {
      console.error('Error adding station:', error);
      alert('❌ Failed to add station. Please try again.');
    }
  };

  return (
    <>
      <SEO />
      <Analytics />
      
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 max-w-6xl mx-auto bg-white w-full">
          <Header 
            selectedState={selectedState} 
            setSelectedState={setSelectedState} 
            filteredCount={filteredStations.length} 
          />
          
          <TabNav activeTab={activeTab} setActiveTab={setActiveTab} />
          
          {(activeTab === 'list' || activeTab === 'map') && (
            <FuelTypeSelector 
              selectedFuelType={selectedFuelType} 
              setSelectedFuelType={setSelectedFuelType} 
            />
          )}

          {activeTab === 'list' && (
            <>
              <PriceStats lowestPrice={lowestPrice} averagePrice={averagePrice} />
              
              <SearchBar 
                searchQuery={searchQuery} 
                setSearchQuery={setSearchQuery} 
                selectedState={selectedState}
                setShowAddStationForm={setShowAddStationForm}
                resultsCount={filteredStations.length}
              />

              {filteredStations.length > 0 && (
                <PageSizeSelector 
                  itemsPerPage={itemsPerPage} 
                  setItemsPerPage={setItemsPerPage}
                  setCurrentPage={setCurrentPage}
                />
              )}

              <div className="px-4 pb-4 space-y-3">
                {filteredStations.length === 0 && stations.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-2">
                      <svg className="w-16 h-16 mx-auto mb-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <p className="text-gray-600 font-medium">Loading stations...</p>
                    <p className="text-sm text-gray-500 mt-1">This will only take a moment</p>
                  </div>
                ) : filteredStations.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">No stations found {searchQuery ? `matching "${searchQuery}"` : `in ${selectedState}`}</p>
                    <button 
                      onClick={() => {
                        setSearchQuery('');
                        setShowAddStationForm(true);
                      }}
                      className="mt-4 text-green-600 hover:text-green-700 font-medium"
                    >
                      {searchQuery ? 'Clear search' : 'Be the first to add a station!'}
                    </button>
                  </div>
                ) : (
                  <>
                    {paginatedStations.map(station => (
                      <StationCard
                        key={station.id}
                        station={station}
                        selectedFuelType={selectedFuelType}
                        lowestPrice={lowestPrice}
                        setShowReportForm={setShowReportForm}
                      />
                    ))}

                    {filteredStations.length > itemsPerPage && (
                      <Pagination 
                        currentPage={currentPage}
                        totalPages={totalPages}
                        setCurrentPage={setCurrentPage}
                        startIndex={startIndex}
                        endIndex={endIndex}
                        totalItems={filteredStations.length}
                        itemsPerPage={itemsPerPage}
                      />
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {activeTab === 'map' && (
            <MapView 
              selectedState={selectedState}
              selectedFuelType={selectedFuelType}
              filteredStations={filteredStations}
              lowestPrice={lowestPrice}
            />
          )}
        </div>
        <Footer />
      </div>

      {/* Modals - Rendered at root level for proper z-index */}
      {(() => {
        console.log('showReportForm state:', showReportForm);
        console.log('Total stations count:', stations.length);
        
        if (showReportForm) {
          const station = stations.find(s => s.id === showReportForm);
          console.log('Found station:', station);
          
          if (station) {
            return (
              <ReportForm 
                station={station}
                selectedFuelType={selectedFuelType}
                onClose={() => setShowReportForm(false)}
                onSubmit={handleReportPrice}
              />
            );
          } else {
            console.error('Station not found with ID:', showReportForm);
            console.error('Available station IDs:', stations.map(s => s.id));
            return null;
          }
        }
        return null;
      })()}

      {showAddStationForm && (
        <AddStationForm 
          selectedState={selectedState}
          onClose={() => setShowAddStationForm(false)}
          onSubmit={handleAddStation}
        />
      )}
    </>
  );
}

export default App;
